var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
export class CacheService {
    constructor(moduleKey) {
        this.moduleKey = moduleKey;
        this.useLocalStorage = false;
        this.inMemoryCache = {};
        this.domain = '';
        this.cacheInstance = null; // Cache instance for synchronous access
        this.CONSTANTS = {
            SIGNAGE_CACHE: 'signage-cache',
        };
        console.log(`CacheService::constructor - Creating instance for module: ${moduleKey}`);
        this.useLocalStorage = this.isLocalStorageAvailable(); // Explicitly disabling localStorage for fallback testing
        console.log(`CacheService::constructor - useLocalStorage: ${this.useLocalStorage}`);
        this.domain = this.getDomainFromURL();
        console.log(`CacheService::constructor - domain: ${this.domain}`);
        this.initialize();
    }
    /**
     * Singleton access method for module-specific instances.
     */
    static getInstance(moduleKey) {
        if (!CacheService.instances[moduleKey]) {
            CacheService.instances[moduleKey] = new CacheService(moduleKey);
        }
        return CacheService.instances[moduleKey];
    }
    /*
     * Initializes the cache synchronously.
     */
    initialize() {
        console.log(`CacheService::initialize - Starting initialization, useLocalStorage: ${this.useLocalStorage}`);
        // If using Cache API, sync data from Cache API to localStorage in the background
        if (!this.useLocalStorage) {
            console.log('CacheService::initialize - Using Cache API, syncing to localStorage...');
            this.syncCacheApiToLocalStorage();
            // Always load from localStorage for immediate access
            this.loadFromLocalStorage();
        }
        else {
            this.loadFromLocalStorage();
            // If using localStorage, preload data into inMemoryCache
            this.syncCacheApiToLocalStorage();
        }
        console.log('CacheService::initialize - Initialization complete');
    }
    /**
     * Syncs data from Cache API to localStorage in the background.
     * This makes subsequent get() calls synchronous.
     */
    syncCacheApiToLocalStorage() {
        console.log('CacheService::syncCacheApiToLocalStorage - Starting sync...');
        caches
            .open(this.CONSTANTS.SIGNAGE_CACHE)
            .then((cache) => {
            cache.keys().then((cacheKeys) => {
                console.log(`CacheService::syncCacheApiToLocalStorage - Found ${cacheKeys.length} cache entries`);
                cacheKeys.forEach((request) => __awaiter(this, void 0, void 0, function* () {
                    try {
                        let key = request.url;
                        // Extract pathname from URL
                        try {
                            const url = new URL(request.url);
                            key = url.pathname;
                        }
                        catch (_a) {
                            // Use as-is if URL parsing fails
                        }
                        const response = yield cache.match(request);
                        if (response) {
                            const text = yield response.text();
                            const namespacedKey = this.buildNamespacedKey(key);
                            // Store in localStorage for synchronous access
                            localStorage.setItem(namespacedKey, text);
                            // Also update in-memory cache
                            try {
                                this.inMemoryCache[namespacedKey] =
                                    text && text !== 'undefined' ? JSON.parse(text) : null;
                            }
                            catch (e) {
                                this.inMemoryCache[namespacedKey] = text;
                            }
                            console.log(`CacheService::syncCacheApiToLocalStorage - Synced ${namespacedKey}`);
                        }
                    }
                    catch (error) {
                        console.error('CacheService::syncCacheApiToLocalStorage - Error syncing entry:', error);
                    }
                }));
            });
        })
            .catch((error) => {
            console.error('CacheService::syncCacheApiToLocalStorage - Error:', error);
        });
    }
    /**
     * Preloads data from localStorage into inMemoryCache.
     */
    loadFromLocalStorage() {
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key.startsWith(this.getNamespacePrefix())) {
                const value = localStorage.getItem(key);
                if (value && value !== 'undefined') {
                    try {
                        this.inMemoryCache[key] = JSON.parse(value);
                    }
                    catch (e) {
                        this.inMemoryCache[key] = value;
                    }
                }
            }
        }
    }
    /**
     * Gets the Cache API instance synchronously.
     * This will be called lazily on first access.
     */
    getCacheInstance() {
        if (!this.cacheInstance && !this.useLocalStorage) {
            // Note: This is a limitation - Cache API is inherently async
            // We'll need to work around this by returning null and falling back to localStorage
            console.warn('CacheService::getCacheInstance - Cache API is async, falling back to localStorage for this operation');
        }
        return this.cacheInstance;
    }
    /**
     * Returns true if the cache has finished loading all data from Cache API.
     */
    isCacheReady() {
        return true; // Always ready since we removed async preloading
    }
    /**
     * Sets a value in the in-memory cache and both localStorage and Cache API.
     * This method is synchronous from the caller's perspective.
     */
    set(key, value) {
        const namespacedKey = this.buildNamespacedKey(key);
        // Always store in memory first
        this.inMemoryCache[namespacedKey] = value;
        // Always store in localStorage for synchronous access
        localStorage.setItem(namespacedKey, JSON.stringify(value));
        // Also update Cache API in background (async, non-blocking)
        if (!this.useLocalStorage) {
            caches.open(this.CONSTANTS.SIGNAGE_CACHE).then((cache) => {
                cache
                    .put(key.trim(), new Response(JSON.stringify(value)))
                    .then(() => { })
                    .catch((err) => {
                    console.warn('CacheService::Failed to update Cache API:', err);
                });
            });
        }
    }
    /**
     * Gets a value from the in-memory cache synchronously.
     * Falls back to localStorage, then returns null if not found.
     */
    get(key) {
        const namespacedKey = this.buildNamespacedKey(key);
        // First check in-memory cache
        if (this.inMemoryCache[namespacedKey]) {
            return this.inMemoryCache[namespacedKey];
        }
        // Fallback to localStorage (which is synchronous)
        const storedValue = localStorage.getItem(namespacedKey);
        if (storedValue && storedValue !== 'undefined') {
            const parsedValue = JSON.parse(storedValue);
            // Cache in memory for next time
            this.inMemoryCache[namespacedKey] = parsedValue;
            return parsedValue;
        }
        return null;
    }
    /**
     * Deletes a value from the in-memory cache and the underlying storage.
     */
    delete(key) {
        const namespacedKey = this.buildNamespacedKey(key);
        delete this.inMemoryCache[namespacedKey];
        if (this.useLocalStorage) {
            localStorage.removeItem(namespacedKey);
        }
        else {
            // Use the key directly like in the set method
            caches.open(this.CONSTANTS.SIGNAGE_CACHE).then((cache) => {
                cache
                    .delete(key.trim())
                    .catch((err) => console.warn('CacheService::Failed to delete from Cache API:', err));
            });
        }
    }
    clearAllClientCache() {
        try {
            // Clear Local Storage
            localStorage.clear();
            console.log('Local Storage cleared.');
            // Clear Session Storage
            sessionStorage.clear();
            console.log('Session Storage cleared.');
            // Asynchronous background tasks for Query Cache and API Cache
            if (window.caches) {
                caches
                    .keys()
                    .then((cacheNames) => {
                    for (const cacheName of cacheNames) {
                        caches.delete(cacheName).then(() => {
                            console.log(`Query/Cache Storage '${cacheName}' cleared.`);
                        });
                    }
                })
                    .catch((err) => {
                    console.error('Error clearing Cache Storage:', err);
                });
            }
            else {
                console.warn('Cache API is not supported in this browser.');
            }
            // API Cache (Service Worker)
            if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
                navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHE' });
                console.log('Service Worker API cache clearing triggered.');
            }
            else {
                console.log('No active Service Worker found.');
            }
            console.log('Synchronous cache clearing complete. Asynchronous tasks running in background.');
        }
        catch (error) {
            console.error('Error clearing client caches:', error);
        }
    }
    /**
     * Checks if localStorage is available in the current environment.
     */
    isLocalStorageAvailable() {
        try {
            const testKey = '__storage_test__';
            localStorage.setItem(testKey, testKey);
            localStorage.removeItem(testKey);
            return true;
        }
        catch (e) {
            console.warn('CacheService::Local Storage is not available. Using Cache API instead.');
            return false;
        }
    }
    /**
     * Gets the domain from the current URL.
     */
    getDomainFromURL() {
        try {
            return new URL(window.location.href).hostname;
        }
        catch (e) {
            console.warn('CacheService::Failed to retrieve domain. Defaulting to "unknown-domain".');
            return 'unknown-domain';
        }
    }
    /**
     * Builds a namespaced key for the cache.
     */
    buildNamespacedKey(key) {
        return `${this.getNamespacePrefix()}:${key.trim()}`;
    }
    /**
     * Constructs the namespace prefix using the domain and module key.
     */
    getNamespacePrefix() {
        return `${this.domain}:${this.moduleKey}`;
    }
}
CacheService.instances = {};
